import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { HomeworkItem } from '../../types';
import { assessmentFacts } from '../../utils/assessmentDetail';

/**
 * An assessment's key facts - teacher, type, subject, class, dates, timing and
 * marks - as a two-column grid of labelled tiles. Shared by the native and web
 * detail screens so a parent reads the same thing on both.
 */
export function AssessmentFactsGrid({ item }: { item: HomeworkItem }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const facts = assessmentFacts(item);

  return (
    <View style={styles.grid}>
      {facts.map(fact => (
        <View key={fact.label} style={styles.tile}>
          <View style={[styles.iconWrap, fact.alert && styles.iconWrapAlert]}>
            <FeatherIcon name={fact.icon} size={15} color={fact.alert ? '#DC2626' : colors.primary} />
          </View>
          <View style={styles.textWrap}>
            <Text style={styles.label}>{fact.label}</Text>
            <Text style={[styles.value, fact.alert && styles.valueAlert]} numberOfLines={2}>
              {fact.value}
              {fact.alert ? ' · Overdue' : ''}
            </Text>
          </View>
        </View>
      ))}
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    grid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginHorizontal: -6,
    },
    tile: {
      flexDirection: 'row',
      alignItems: 'center',
      width: '50%',
      minWidth: 150,
      flexGrow: 1,
      paddingHorizontal: 6,
      paddingVertical: 8,
    },
    iconWrap: {
      width: 32,
      height: 32,
      borderRadius: 10,
      backgroundColor: `${colors.primary}14`,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 10,
    },
    iconWrapAlert: {
      backgroundColor: '#FEE2E2',
    },
    textWrap: {
      flex: 1,
    },
    label: {
      fontSize: 11,
      fontWeight: '600',
      color: colors.textMuted,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    value: {
      fontSize: 14,
      fontWeight: '700',
      color: colors.textPrimary,
      marginTop: 2,
    },
    valueAlert: {
      color: '#DC2626',
    },
  });
